import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchPendingLeaves, 
  fetchPendingClaims, 
  clearErrors 
} from '../../../store/slices/approvalsSlice';
import { fetchEmployees } from '../../../store/slices/authSlice';
import ApprovalModal from './components/ApprovalModal';
import './Approvals.scss';

const Approvals = () => {
  const [activeTab, setActiveTab] = useState('leaves');
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [allData, setAllData] = useState({
    leaves: [],
    claims: []
  });
  
  const dispatch = useDispatch();
  const { 
    leaves: { data: leavesData, loading: leavesLoading, error: leavesError, updatingId: updatingLeaveId },
    claims: { data: claimsData, loading: claimsLoading, error: claimsError, updatingId: updatingClaimId }
  } = useSelector(state => state.approvals);
  
  // Get employees data from auth state for name mapping
  const { employees } = useSelector(state => state.auth);

  // Initial data fetch - get ALL data first
  useEffect(() => {
    const fetchAllData = async () => {
      await dispatch(fetchPendingLeaves('All'));
      await dispatch(fetchPendingClaims('All'));
    };
    
    fetchAllData();
    
    // Clear errors when component unmounts
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch]);

  // Fetch employees data separately to avoid infinite loop
  useEffect(() => {
    if (!employees || employees.length === 0) {
      dispatch(fetchEmployees());
    }
  }, [dispatch, employees]); // Include employees in dependency array

  // Update all data when we get new data
  useEffect(() => {
    setAllData({
      leaves: leavesData || [],
      claims: claimsData || []
    });
  }, [leavesData, claimsData]);

  // Filter data based on current status filter
  const filteredData = useMemo(() => {
    const leaves = allData.leaves || [];
    const claims = allData.claims || [];

    return {
      leaves: statusFilter === 'All' 
        ? leaves 
        : leaves.filter(leave => leave.status === statusFilter),
      claims: statusFilter === 'All'
        ? claims
        : claims.filter(claim => claim.status === statusFilter)
    };
  }, [allData, statusFilter]);

  // Calculate counts for each status
  const counts = useMemo(() => {
    const result = {
      leaves: {
        All: allData.leaves?.length || 0,
        Pending: 0,
        Approved: 0,
        Rejected: 0
      },
      claims: {
        All: allData.claims?.length || 0,
        Pending: 0,
        Approved: 0,
        Rejected: 0
      }
    };

    // Count leaves by status
    if (allData.leaves && Array.isArray(allData.leaves)) {
      allData.leaves.forEach(leave => {
        if (leave.status && result.leaves.hasOwnProperty(leave.status)) {
          result.leaves[leave.status]++;
        }
      });
    }

    // Count claims by status
    if (allData.claims && Array.isArray(allData.claims)) {
      allData.claims.forEach(claim => {
        if (claim.status && result.claims.hasOwnProperty(claim.status)) {
          result.claims[claim.status]++;
        }
      });
    }

    return result;
  }, [allData]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    // Refresh data after modal closes (in case statuses were updated)
    dispatch(fetchPendingLeaves('All'));
    dispatch(fetchPendingClaims('All'));
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const getLeaveTypeName = (type) => {
    const leaveTypes = {
      'Vacation': 'Vacation',
      'Sick': 'Sick Leave',
      // Legacy mappings for backward compatibility
      'annual': 'Vacation',
      'sick': 'Sick Leave',
      'casual': 'Vacation',
      'personal': 'Vacation',
      'unpaid': 'Vacation'
    };
    return leaveTypes[type] || type;
  };

  // Helper function to get employee name from empID or empEmail
  const getEmployeeName = useMemo(() => {
    // Create a memoized function that uses the current employees array
    return (item) => {
      if (!employees || employees.length === 0) {
        return item.employeeName || item.name || 'Loading...';
      }

      // Try to find by empID first
      if (item.empID) {
        const employee = employees.find(emp => emp.empID === item.empID);
        if (employee) {
          return employee.name || employee.displayName || employee.email;
        }
      }

      // Then try by email (empEmail or email field)
      const email = item.empEmail || item.email;
      if (email) {
        const employee = employees.find(emp => emp.email === email);
        if (employee) {
          return employee.name || employee.displayName || email;
        }
      }

      // Fallback to existing fields or unknown
      return item.employeeName || item.name || 'Unknown Employee';
    };
  }, [employees]);

  // Helper function to get employee email
  const getEmployeeEmail = (item) => {
    return item.empEmail || item.email || '';
  };

  const renderStatusFilterButtons = () => {
    const statuses = ['All', 'Pending', 'Approved', 'Rejected'];
    const activeData = activeTab === 'leaves' ? counts.leaves : counts.claims;
    
    return (
      <div className="status-filter-group">
        <span className="filter-label">Filter by status:</span>
        <div className="button-group">
          {statuses.map(status => {
            const count = activeData[status] || 0;
            return (
              <button
                key={status}
                className={`btn-group-item ${statusFilter === status ? 'active' : ''}`}
                onClick={() => handleStatusFilterChange(status)}
              >
                {status} <span className="count-badge">({count})</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLeavesTab = () => {
    if (leavesLoading && !filteredData.leaves.length) {
      return (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading leaves...</p>
        </div>
      );
    }

    if (leavesError) {
      return (
        <div className="error-message">
          Error loading leaves: {leavesError}
        </div>
      );
    }

    if (!filteredData.leaves || filteredData.leaves.length === 0) {
      return (
        <div className="empty-state">
          No leave requests found with status: {statusFilter}
        </div>
      );
    }

    return (
      <div className="ui-table-wrap">
      <table className="approvals-table ui-table">
        <thead>
          <tr>
            <th>Employee</th>
            <th>Leave Type</th>
            <th>From</th>
            <th>To</th>
            <th>Days</th>
            <th>Applied On</th>
            <th>Status</th>
            <th>Remarks</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.leaves.map(leave => (
            <tr key={leave.id} className={leave.status.toLowerCase() !== 'pending' ? 'processed-item' : ''}>
              <td className="employee-cell">
                <div>{getEmployeeName(leave)}</div>
                <div className="email">{getEmployeeEmail(leave)}</div>
              </td>
              <td>{getLeaveTypeName(leave.leaveType)}</td>
              <td>{formatDate(leave.startDate)}</td>
              <td>{formatDate(leave.endDate)}</td>
              <td>{leave.leaveDays}</td>
              <td>{formatDate(leave.appliedDate || leave.startDate)}</td>
              <td>
                <span className={`status-badge status-${leave.status.toLowerCase()}`}>
                  {leave.status}
                </span>
              </td>
              <td className="remarks-cell">
                <div className="remarks-content">{leave.remarks || leave.leaveRemarks || 'No remarks'}</div>
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    className="view-doc-button"
                    onClick={() => handleViewItem(leave)}
                    disabled={updatingLeaveId === leave.id}
                  >
                    Review
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  const renderClaimsTab = () => {
    if (claimsLoading && !filteredData.claims.length) {
      return (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading claims...</p>
        </div>
      );
    }

    if (claimsError) {
      return (
        <div className="error-message">
          Error loading claims: {claimsError}
        </div>
      );
    }

    if (!filteredData.claims || filteredData.claims.length === 0) {
      return (
        <div className="empty-state">
          No claim requests found with status: {statusFilter}
        </div>
      );
    }

    return (
      <div className="ui-table-wrap">
      <table className="approvals-table ui-table">
        <thead>
          <tr>
            <th>Employee</th>
            <th>Claim Type</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Status</th>
            <th>Submitted On</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.claims.map(claim => (
            <tr key={claim.id} className={claim.status.toLowerCase() !== 'pending' ? 'processed-item' : ''}>
              <td className="employee-cell">
                <div>{getEmployeeName(claim)}</div>
                <div className="email">{getEmployeeEmail(claim)}</div>
              </td>
              <td>{claim.claimType || claim.expensePurpose || claim.title || 'General'}</td>
              <td>{claim.currency || '$'} {claim.amount ? parseFloat(claim.amount).toFixed(2) : '0.00'}</td>
              <td>{formatDate(claim.date || claim.expenseDate)}</td>
              <td>
                <span className={`status-badge status-${claim.status.toLowerCase()}`}>
                  {claim.status}
                </span>
              </td>
              <td>{formatDate(claim.submittedDate || claim.claimDate)}</td>
              <td className="remarks-cell">
                <div className="remarks-content">{claim.description || claim.remarks || 'No description'}</div>
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    className="view-doc-button"
                    onClick={() => handleViewItem(claim)}
                    disabled={updatingClaimId === claim.id}
                  >
                    Review
                  </button>
                  {claim.documentUrl || claim.supportingDoc ? (
                    <a 
                      href={claim.documentUrl || claim.supportingDoc}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="view-doc-button"
                    >
                      View Doc
                    </a>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  return (
    <div className="approvals-container">
      <div className="page-header">
        <h1>Approvals</h1>
        <p>Manage leave and claim requests from employees</p>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'leaves' ? 'active' : ''}`}
          onClick={() => handleTabChange('leaves')}
        >
          Leave Requests ({counts.leaves.All})
          {counts.leaves.Pending > 0 && (
            <span className="pending-badge">{counts.leaves.Pending} pending</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'claims' ? 'active' : ''}`}
          onClick={() => handleTabChange('claims')}
        >
          Claim Requests ({counts.claims.All})
          {counts.claims.Pending > 0 && (
            <span className="pending-badge">{counts.claims.Pending} pending</span>
          )}
        </button>
      </div>

      {renderStatusFilterButtons()}

      <div className="content-section">
        {activeTab === 'leaves' ? renderLeavesTab() : renderClaimsTab()}
      </div>

      <div className="refresh-section">
        <button 
          className="refresh-button" 
          onClick={() => {
            dispatch(fetchPendingLeaves('All'));
            dispatch(fetchPendingClaims('All'));
          }}
          disabled={leavesLoading || claimsLoading}
        >
          Refresh Data
        </button>
      </div>

      {modalOpen && (
        <ApprovalModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          item={selectedItem}
          type={activeTab === 'leaves' ? 'leave' : 'claim'}
          isUpdating={
            (activeTab === 'leaves' && updatingLeaveId === selectedItem?.id) || 
            (activeTab === 'claims' && updatingClaimId === selectedItem?.id)
          }
        />
      )}
    </div>
  );
};

export default Approvals; 